import React from 'react';
import { SuccessOrderConfirmationContainer } from './success-order-confirmation/success-order-confirmation-container';
import { FailureScreenContainer } from './order-confirmation-failure/failure-screen-container';
import { getProvisioningUrl } from './utils';
import { useApplicationContext } from '@css-xp/base';

export type OrderConfirmationProps = {
  newSiteOrderId: string;
  transactionAccountId: string;
  orderResponse: any;
  onTryAgainClick: () => void;
  isDesignIteration1Enabled?: boolean;
};

export enum successVariants {
  NEW_SITE = 'NEW_SITE',
  EXISTING_SITE = 'EXISTING_SITE',
  NEW_AND_EXISTING_SITE = 'NEW_AND_EXISTING_SITE',
}

export enum FailureVariants {
  COMPLETE = 'COMPLETE',
  PARTIAL = 'PARTIAL', //it is more like partial without new site success
  PARTIAL_WITH_ONLY_NEW_SITE_SUCCESS = 'PARTIAL_WITH_ONLY_NEW_SITE_SUCCESS',
  PARTIAL_WITH_SOME_EXISTING_AND_NEW_SITE_SUCCESS = 'PARTIAL_WITH_SOME_EXISTING_AND_NEW_SITE_SUCCESS',
}

const getIsExistingSiteSuccessItem = (item: any) => {
  if (item.orderType === 'CREATION_ORDER') {
    return !!item?.metadata?.siteId;
  } else return true;
};

const getIsExistingSiteNewOrderSuccessItem = (item: any) => {
  if (item.orderType === 'CREATION_ORDER') {
    return item.metadata && item.metadata.siteId;
  } else return false;
};

export const OrderConfirmationContainer = ({
  newSiteOrderId,
  transactionAccountId,
  orderResponse,
  onTryAgainClick,
  isDesignIteration1Enabled,
}: OrderConfirmationProps) => {
  const { email } = useApplicationContext();

  if (Object.keys(orderResponse).length === 0) return null;
  const orderItems = orderResponse.items;
  let failureVariant: FailureVariants;

  const failureItemIds = orderItems
    .filter((item: any) => item.orderStatus === 'rejected')
    .map((item: any) => {
      if (item?.itemId) {
        return item.ItemId;
      } else if (item?.entitlementId) {
        return item.entitlementId;
      } else {
        return item.productId;
      }
    })
    .filter((id: string) => id !== undefined);

  //logic for appropriate variant
  const isCompleteSuccessOrder = orderItems.every((item: any) => item.orderStatus === 'fulfilled');

  //Complete failure order
  const isCompleteFailureOrder = orderItems.every((item: any) => item.orderStatus === 'rejected');
  let provisioningUrl: string;
  if (newSiteOrderId !== '') {
    provisioningUrl = getProvisioningUrl(newSiteOrderId, transactionAccountId, email);
  }
  const onGetStartedClick = () => window.open(provisioningUrl);

  if (isCompleteSuccessOrder) {
    //now check for new, existing, and new_with_existing site order
    let variant: successVariants;

    const isNewSiteOrderItem = newSiteOrderId !== '';
    const isExistingSiteOrderItem = orderItems.some((item: any) => {
      return getIsExistingSiteSuccessItem(item);
    });

    const isExistingSiteNewOrderItem = orderItems.some((item: any) => {
      return getIsExistingSiteNewOrderSuccessItem(item);
    });
    if (isExistingSiteOrderItem) {
      variant = isNewSiteOrderItem ? successVariants.NEW_AND_EXISTING_SITE : successVariants.EXISTING_SITE;
    } else {
      variant = successVariants.NEW_SITE;
    }

    return (
      <SuccessOrderConfirmationContainer
        variant={variant}
        onGetStartedClick={onGetStartedClick}
        isExistingSiteNewOrderItem={isExistingSiteNewOrderItem}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
      />
    );
  }

  if (isCompleteFailureOrder) {
    return (
      <FailureScreenContainer
        variant={FailureVariants.COMPLETE}
        onTryAgainClick={onTryAgainClick}
        failureItemIds={failureItemIds}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
      />
    );
  }

  // partial failure case
  const successOrderItems = orderItems.filter((item: any) => item.orderStatus === 'fulfilled');

  // shows if new site order passed
  const isNewSiteSuccessOrderItem = successOrderItems?.some((item: any) => {
    if (item.orderType === 'CREATION_ORDER') {
      return !item?.metadata?.siteId;
    } else return false;
  });

  //shows id existing site order placed
  //same update for metadata
  const isExistingSiteSuccessOrderPlaced = successOrderItems?.some((item: any) => {
    return getIsExistingSiteSuccessItem(item);
  });

  const isExistingSiteNewOrderPlaced = successOrderItems?.some((item: any) => {
    return getIsExistingSiteNewOrderSuccessItem(item);
  });

  if (isNewSiteSuccessOrderItem) {
    failureVariant = isExistingSiteSuccessOrderPlaced
      ? FailureVariants.PARTIAL_WITH_SOME_EXISTING_AND_NEW_SITE_SUCCESS
      : FailureVariants.PARTIAL_WITH_ONLY_NEW_SITE_SUCCESS;
  } else {
    failureVariant = FailureVariants.PARTIAL;
  }

  return (
    <FailureScreenContainer
      variant={failureVariant}
      onGetStartedClick={onGetStartedClick}
      failureItemIds={failureItemIds}
      isExistingSiteNewOrderPlaced={isExistingSiteNewOrderPlaced}
      isDesignIteration1Enabled={isDesignIteration1Enabled}
    />
  );
};
